/**
 * BPM (fluxogramas): desenho do fluxo, iniciar fluxo, acompanhar execuções e o passo do fluxo na atividade.
 */
import { useTableSort } from "@/lib/sort";
import { useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, qs } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { Link } from "@/lib/router";
import { useLookups } from "@/lib/catalog";
import { Badge, Card, Empty, ErrorBox, Field, FormGrid, Loading, Modal, UserSelect, useAct } from "./kit";
import { Icon } from "./icons";
import { confirmar } from "./feedback";

// ------------------------------------------------------------------ tipos

export type NodeType = "inicio" | "tarefa" | "decisao" | "paralelo" | "juntar" | "espera" | "acao" | "fim";
export interface NodeData {
  label?: string; title?: string; description?: string; typeKey?: string; role?: string; reviewerRole?: string;
  priority?: "baixa" | "media" | "alta" | "urgente"; due?: { base: "inicio" | "passo" | "fatal"; days: number }; setFatal?: boolean;
  outcomes?: string[]; checklist?: string[];
  source?: "resultado" | "campo"; fromNode?: string; field?: string;
  waitDays?: number; waitBase?: "agora" | "fatal";
  action?: "avisar" | "mover_etapa"; toRoles?: string[]; message?: string; stageId?: string;
}
export interface FlowNode { id: string; type: NodeType; x: number; y: number; data: NodeData }
export interface FlowEdge { id: string; from: string; to: string; when?: string }
export interface FlowGraph { nodes: FlowNode[]; edges: FlowEdge[]; settings?: { precreate?: boolean } }
export interface FlowRole { key: string; label: string; default: string; userId?: string | null }
export interface FlowTrigger { kind: string; areaId?: string | null; subjectTypeId?: string | null; stageId?: string | null; typeId?: string | null; days?: number | null }
export interface BpmCatalog {
  nodeTypes: NodeType[]; triggers: Array<{ kind: string; label: string; filters: string[] }>;
  roleDefaults: Array<{ key: string; label: string }>; templates: Array<{ key: string; name: string; description: string }>;
  fields: Array<{ key: string; label: string }>;
}

export const NODE_LABEL: Record<NodeType, string> = {
  inicio: "Início", tarefa: "Tarefa", decisao: "Decisão", paralelo: "Paralelo", juntar: "Juntar", espera: "Espera", acao: "Ação automática", fim: "Fim",
};
export const NODE_HINT: Record<NodeType, string> = {
  inicio: "Onde o fluxo começa (só um).",
  tarefa: "Vira uma atividade para a pessoa do papel. Papel vazio ao iniciar = passo pulado.",
  decisao: "Escolhe o caminho pelo resultado de uma tarefa (ex.: Aprovar/Devolver) ou por um dado do processo.",
  paralelo: "Abre vários caminhos ao mesmo tempo.",
  juntar: "Espera todos os caminhos chegarem antes de seguir.",
  espera: "Para por alguns dias úteis (ou até X dias antes do prazo fatal).",
  acao: "Faz algo sozinho: avisar pessoas ou mover a etapa do kanban.",
  fim: "Termina o fluxo.",
};

export function useBpmCatalog() {
  return useQuery({ queryKey: ["bpm-catalogo"], queryFn: () => api.get<BpmCatalog>("/bpm/catalogo"), staleTime: 300_000 });
}

// ------------------------------------------------------------------ desenho (SVG)

export const SIZE: Record<NodeType, { w: number; h: number }> = {
  inicio: { w: 150, h: 40 }, fim: { w: 150, h: 40 }, tarefa: { w: 190, h: 62 }, decisao: { w: 150, h: 74 },
  paralelo: { w: 150, h: 30 }, juntar: { w: 150, h: 30 }, espera: { w: 160, h: 48 }, acao: { w: 170, h: 52 },
};

const COLOR: Record<NodeType, { fill: string; stroke: string }> = {
  inicio: { fill: "var(--green-50)", stroke: "var(--green)" }, fim: { fill: "var(--surface)", stroke: "var(--muted)" },
  tarefa: { fill: "var(--card)", stroke: "var(--primary)" }, decisao: { fill: "var(--amber-50)", stroke: "var(--amber)" },
  paralelo: { fill: "var(--blue-50)", stroke: "var(--blue)" }, juntar: { fill: "var(--blue-50)", stroke: "var(--blue)" },
  espera: { fill: "var(--violet-50)", stroke: "var(--purple)" }, acao: { fill: "var(--violet-50)", stroke: "var(--purple)" },
};

export type StepMark = "ativo" | "aguardando" | "previsto" | "dispensado" | "concluido" | "pulado" | "erro" | "cancelado";
const MARK: Record<StepMark, string> = { ativo: "var(--blue)", aguardando: "var(--purple)", previsto: "var(--amber)", dispensado: "var(--muted-l)", concluido: "var(--green)", pulado: "var(--muted-l)", erro: "var(--red)", cancelado: "var(--red)" };

function nodeText(n: FlowNode): string { return n.data.title || n.data.label || NODE_LABEL[n.type]; }
function clip(s: string, max: number): string { return s.length > max ? `${s.slice(0, max - 1)}…` : s; }
function wrap(s: string, max: number, lines: number): string[] {
  const words = s.split(/\s+/); const out: string[] = []; let cur = "";
  for (const w of words) { if ((cur + " " + w).trim().length > max && cur) { out.push(cur); cur = w; } else cur = (cur + " " + w).trim(); }
  if (cur) out.push(cur);
  if (out.length > lines) { out.length = lines; out[lines - 1] = clip(out[lines - 1]!, max - 1) + "…"; }
  return out;
}

export function edgePath(g: FlowGraph, e: FlowEdge): { d: string; lx: number; ly: number } | null {
  const a = g.nodes.find((n) => n.id === e.from), b = g.nodes.find((n) => n.id === e.to);
  if (!a || !b) return null;
  const sa = SIZE[a.type], sb = SIZE[b.type];
  const sx = a.x + sa.w / 2, sy = a.y + sa.h, tx = b.x + sb.w / 2, ty = b.y;
  if (ty >= sy + 10) {
    const dy = Math.max(30, (ty - sy) / 2);
    return { d: `M${sx},${sy} C${sx},${sy + dy} ${tx},${ty - dy} ${tx},${ty}`, lx: (sx + tx) / 2, ly: (sy + ty) / 2 };
  }
  // seta de volta (ex.: Devolver): sai pela lateral e contorna
  const left = tx <= sx;
  const ax = left ? a.x : a.x + sa.w, ay = a.y + sa.h / 2;
  const bx = left ? b.x : b.x + sb.w, by = b.y + sb.h / 2;
  const off = left ? -90 : 90;
  const cx = (left ? Math.min(ax, bx) : Math.max(ax, bx)) + off;
  return { d: `M${ax},${ay} C${cx},${ay} ${cx},${by} ${bx},${by}`, lx: cx - (left ? -6 : 6), ly: (ay + by) / 2 };
}

export function FlowDiagram({ graph, marks = {}, who = {}, selected, onNodeDown, onNodeClick, onEdgeClick, onHandleDown, onBgDown, temp, selectedEdge, height }: {
  graph: FlowGraph; marks?: Record<string, StepMark>; who?: Record<string, string>; selected?: string | null; selectedEdge?: string | null;
  onNodeDown?: (id: string, ev: React.PointerEvent) => void; onNodeClick?: (id: string) => void; onEdgeClick?: (id: string) => void;
  onHandleDown?: (id: string, ev: React.PointerEvent) => void; onBgDown?: (ev: React.PointerEvent) => void;
  temp?: { x1: number; y1: number; x2: number; y2: number } | null; height?: number;
}): JSX.Element {
  const W = Math.max(900, ...graph.nodes.map((n) => n.x + SIZE[n.type].w + 140));
  const H = height ?? Math.max(420, ...graph.nodes.map((n) => n.y + SIZE[n.type].h + 80));
  return (
    <svg width={W} height={H} style={{ display: "block", userSelect: "none", touchAction: "none" }} onPointerDown={onBgDown}>
      <defs>
        <marker id="bpm-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill="var(--muted)" /></marker>
        <marker id="bpm-arrow-sel" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill="var(--primary)" /></marker>
        <pattern id="bpm-grid" width="20" height="20" patternUnits="userSpaceOnUse"><circle cx="1" cy="1" r="1" fill="var(--border)" /></pattern>
      </defs>
      <rect x={0} y={0} width={W} height={H} fill="url(#bpm-grid)" />
      {graph.edges.map((e) => {
        const p = edgePath(graph, e);
        if (!p) return null;
        const sel = selectedEdge === e.id;
        const label = e.when === "*" ? "senão" : e.when;
        return (
          <g key={e.id} style={{ cursor: onEdgeClick ? "pointer" : "default" }} onPointerDown={(ev) => { if (onEdgeClick) { ev.stopPropagation(); onEdgeClick(e.id); } }}>
            <path d={p.d} fill="none" stroke="transparent" strokeWidth={14} />
            <path d={p.d} fill="none" stroke={sel ? "var(--primary)" : "var(--muted)"} strokeWidth={sel ? 2.5 : 1.6} markerEnd={`url(#${sel ? "bpm-arrow-sel" : "bpm-arrow"})`} />
            {label && (
              <g transform={`translate(${p.lx},${p.ly})`}>
                <rect x={-(label.length * 3.4 + 8)} y={-10} width={label.length * 6.8 + 16} height={20} rx={10} fill="var(--card)" stroke={sel ? "var(--primary)" : "var(--border)"} />
                <text textAnchor="middle" y={4} fontSize={11} fill="var(--text)">{label}</text>
              </g>
            )}
          </g>
        );
      })}
      {temp && <line x1={temp.x1} y1={temp.y1} x2={temp.x2} y2={temp.y2} stroke="var(--primary)" strokeDasharray="5 4" strokeWidth={2} />}
      {graph.nodes.map((n) => {
        const s = SIZE[n.type], c = COLOR[n.type], m = marks[n.id];
        const sel = selected === n.id;
        const stroke = m ? MARK[m] : sel ? "var(--primary-d)" : c.stroke;
        const sw = m || sel ? 3 : 1.5;
        const text = nodeText(n);
        return (
          <g key={n.id} transform={`translate(${n.x},${n.y})`} style={{ cursor: onNodeDown ? "grab" : onNodeClick ? "pointer" : "default", opacity: m === "pulado" ? 0.5 : 1 }}
            onPointerDown={(ev) => { if (onNodeDown) { ev.stopPropagation(); onNodeDown(n.id, ev); } }}
            onClick={(ev) => { if (onNodeClick) { ev.stopPropagation(); onNodeClick(n.id); } }}>
            {n.type === "decisao" ? (
              <polygon points={`${s.w / 2},0 ${s.w},${s.h / 2} ${s.w / 2},${s.h} 0,${s.h / 2}`} fill={c.fill} stroke={stroke} strokeWidth={sw} />
            ) : (
              <rect width={s.w} height={s.h} rx={n.type === "inicio" || n.type === "fim" ? s.h / 2 : n.type === "paralelo" || n.type === "juntar" ? 4 : 8}
                fill={c.fill} stroke={stroke} strokeWidth={sw} strokeDasharray={n.type === "espera" ? "5 3" : undefined} />
            )}
            {n.type === "tarefa" ? (
              <>
                {wrap(text, 26, 2).map((l, i) => <text key={i} x={10} y={18 + i * 15} fontSize={12} fontWeight={600} fill="var(--text)">{l}</text>)}
                <text x={10} y={s.h - 8} fontSize={10.5} fill="var(--muted)">{clip(who[n.id] ?? (n.data.role ? `👤 ${n.data.role}` : "sem papel"), 30)}{n.data.outcomes?.length ? ` · ${n.data.outcomes.join("/")}` : ""}</text>
              </>
            ) : (
              wrap(text, n.type === "decisao" ? 16 : 22, 2).map((l, i, arr) => (
                <text key={i} x={s.w / 2} y={s.h / 2 + 4 + (i - (arr.length - 1) / 2) * 13} textAnchor="middle" fontSize={11.5} fontWeight={n.type === "inicio" || n.type === "fim" ? 600 : 500} fill="var(--text)">{l}</text>
              ))
            )}
            {m && <circle cx={s.w - 6} cy={6} r={6} fill={MARK[m]} />}
            {onHandleDown && n.type !== "fim" && (
              <circle cx={s.w / 2} cy={s.h} r={7} fill="var(--card)" stroke="var(--primary)" strokeWidth={2} style={{ cursor: "crosshair" }}
                onPointerDown={(ev) => { ev.stopPropagation(); onHandleDown(n.id, ev); }}><title>Arraste até outro bloco para ligar</title></circle>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ------------------------------------------------------------------ iniciar fluxo

interface ActiveDef { id: string; name: string; description: string | null; roles: FlowRole[]; trigger: FlowTrigger; usesFatal: boolean }

export function StartFlowButton({ processId, caseId, personId, fatalDueAt, communicationId, label = "Iniciar fluxo", primary }: {
  processId?: string | null; caseId?: string | null; personId?: string | null; fatalDueAt?: string | null; communicationId?: string | null; label?: string; primary?: boolean;
}): JSX.Element | null {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className={`btn ${primary ? "primary" : ""}`} onClick={() => setOpen(true)}><Icon name="flow" size="sm" /> {label}</button>
      {open && <StartFlowModal processId={processId} caseId={caseId} personId={personId} fatalDueAt={fatalDueAt} communicationId={communicationId} onClose={() => setOpen(false)} />}
    </>
  );
}

export function StartFlowModal({ processId, caseId, personId, fatalDueAt, communicationId, onClose, onStarted }: {
  processId?: string | null; caseId?: string | null; personId?: string | null; fatalDueAt?: string | null; communicationId?: string | null; onClose: () => void; onStarted?: (id: string) => void;
}): JSX.Element {
  const defs = useQuery({ queryKey: ["bpm-ativos"], queryFn: () => api.get<ActiveDef[]>("/bpm/definicoes") });
  const [sel, setSel] = useState<string | null>(null);
  const [roles, setRoles] = useState<Record<string, string | null>>({});
  const [fatal, setFatal] = useState(fatalDueAt ?? "");
  const def = defs.data?.find((d) => d.id === sel) ?? null;
  const sug = useQuery({
    queryKey: ["bpm-papeis", sel, processId, caseId, personId], enabled: !!sel,
    queryFn: async () => {
      const r = await api.post<Array<FlowRole & { suggested: string | null }>>("/bpm/papeis-sugeridos", { definitionId: sel, processId, caseId, personId });
      setRoles(Object.fromEntries(r.map((x) => [x.key, x.suggested])));
      return r;
    },
  });
  const start = useAct(() => api.post<{ id: string }>("/bpm/execucoes", { definitionId: sel, processId, caseId: processId ? null : caseId, personId: processId || caseId ? null : personId, roles, fatalDueAt: fatal || null, communicationId: communicationId ?? null }),
    [["bpm-execucoes"], ["atividades"], ["painel"]], (r) => { onStarted?.(r.id); onClose(); }, "Fluxo iniciado");
  const needFatal = !!def?.usesFatal && !fatal;
  return (
    <Modal title="Iniciar fluxo" onClose={onClose} wide footer={<>
      <button className="btn" onClick={onClose}>Cancelar</button>
      <button className="btn primary" disabled={!sel || start.isPending || !sug.data} onClick={() => void (async () => {
        if (needFatal && !(await confirmar("Este fluxo usa o prazo fatal para calcular as datas, mas nenhum foi informado. Iniciar assim mesmo (prazos contados a partir de hoje)?", { confirm: "Iniciar" }))) return;
        start.mutate();
      })()}>Iniciar</button>
    </>}>
      <ErrorBox error={defs.error ?? start.error ?? sug.error} />
      {!defs.data ? <Loading /> : !defs.data.length ? <Empty icon="flow">Nenhum fluxo ativo. O administrador desenha e ativa em BPM / Workflow › Fluxogramas.</Empty> : (
        <div className="stack gap-12">
          <div className="stack gap-8">
            {defs.data.map((d) => (
              <label key={d.id} className={`tpl-card ${sel === d.id ? "on" : ""}`} style={{ cursor: "pointer" }}>
                <input type="radio" checked={sel === d.id} onChange={() => setSel(d.id)} />
                <div className="grow"><div className="strong">{d.name}</div>{d.description && <div className="muted small">{d.description}</div>}</div>
              </label>
            ))}
          </div>
          {def && (
            <>
              <div className="fld-label">Quem faz cada papel</div>
              <div className="muted small">Deixe um papel vazio para pular as tarefas dele (ex.: Revisor 2 vazio = uma revisão só).</div>
              {!sug.data ? <Loading lines={2} /> : (
                <FormGrid cols={2}>
                  {sug.data.map((r) => (
                    <Field key={r.key} label={r.label}><UserSelect value={roles[r.key] ?? null} onChange={(v) => setRoles({ ...roles, [r.key]: v })} emptyLabel="— ninguém (pula) —" /></Field>
                  ))}
                </FormGrid>
              )}
              <FormGrid cols={2}>
                <Field label="Prazo fatal" hint={def.usesFatal ? "As datas das tarefas são contadas para trás a partir dele." : "Opcional."}>
                  <input type="date" className="input" value={fatal} onChange={(e) => setFatal(e.target.value)} />
                </Field>
              </FormGrid>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

// ------------------------------------------------------------------ execuções

export interface RunRow {
  id: string; name: string; status: string; statusNote: string | null; fatalDueAt: string | null; startedAt: string; finishedAt: string | null; startedBy: string;
  process: { id: string; cnjNumber?: string | null; title?: string | null } | null; case: { id: string; number?: number; title?: string } | null; personId: string | null;
  current: Array<{ nodeId: string; status: string; label: string; type: NodeType; assignee: string | null; activityId: string | null; waitUntil: string | null }>;
}

export const RUN_STATUS: Record<string, { label: string; tone: "ok" | "warn" | "bad" | "info" | "muted" }> = {
  ativo: { label: "Em andamento", tone: "info" }, parado: { label: "Parado", tone: "bad" }, concluido: { label: "Concluído", tone: "ok" }, cancelado: { label: "Cancelado", tone: "muted" },
};

function where(r: RunRow): ReactNode {
  if (r.process) return <Link to={`/processos/${r.process.id}`}>{r.process.cnjNumber ?? r.process.title ?? "Processo"}</Link>;
  if (r.case) return <Link to={`/casos/${r.case.id}`}>Caso #{r.case.number} {r.case.title}</Link>;
  if (r.personId) return <Link to={`/pessoas/${r.personId}`}>Pessoa</Link>;
  return "—";
}

function CurrentSteps({ r }: { r: RunRow }): JSX.Element {
  if (!r.current.length) return <span className="muted">—</span>;
  return (
    <div className="stack gap-4">
      {r.current.map((c) => (
        <div key={c.nodeId + (c.activityId ?? "")} className="small">
          {c.activityId ? <Link to={`/atividades/${c.activityId}`}>{c.label}</Link> : <span>{c.label}</span>}
          {c.assignee && <span className="muted"> · {c.assignee}</span>}
          {c.status === "aguardando" && <span className="muted"> · {c.waitUntil ? `até ${fmtDateTime(c.waitUntil)}` : "esperando os outros caminhos"}</span>}
          {c.status === "erro" && <Badge tone="bad">erro</Badge>}
        </div>
      ))}
    </div>
  );
}

/** Fluxos de um processo/caso/pessoa (aba "Fluxos"). */
export function FlowRunsPanel({ processId, caseId, personId, canStart = true }: { processId?: string; caseId?: string; personId?: string; canStart?: boolean }): JSX.Element {
  const runs = useQuery({ queryKey: ["bpm-execucoes", processId, caseId, personId], queryFn: () => api.get<RunRow[]>(`/bpm/execucoes${qs({ processId, caseId, personId })}`) });
  const [open, setOpen] = useState<string | null>(null);
  const list = runs.data ?? [];
  const running = list.filter((r) => r.status === "ativo" || r.status === "parado");
  return (
    <div className="stack gap-16">
      <Card title="Fluxos" actions={canStart ? <StartFlowButton processId={processId} caseId={caseId} personId={personId} primary /> : undefined}>
        <ErrorBox error={runs.error} />
        {!runs.data ? <Loading /> : !list.length ? <Empty icon="flow">Nenhum fluxo aqui ainda. Use “Iniciar fluxo” (ex.: Petição, Recurso, Audiência).</Empty> : (
          <div className="stack gap-12">
            {running.map((r) => <RunCard key={r.id} r={r} onOpen={() => setOpen(r.id)} />)}
            {list.filter((r) => !running.includes(r)).length > 0 && (
              <details>
                <summary className="muted small" style={{ cursor: "pointer" }}>Encerrados ({list.length - running.length})</summary>
                <div className="stack gap-8" style={{ marginTop: 8 }}>
                  {list.filter((r) => !running.includes(r)).map((r) => (
                    <div key={r.id} className="row gap-8 small" style={{ cursor: "pointer" }} onClick={() => setOpen(r.id)}>
                      <Badge tone={RUN_STATUS[r.status]?.tone ?? "muted"}>{RUN_STATUS[r.status]?.label ?? r.status}</Badge>
                      <span className="strong">{r.name}</span><span className="muted">{fmtDateTime(r.startedAt)} → {fmtDateTime(r.finishedAt)}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
      </Card>
      {open && <RunModal id={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function RunCard({ r, onOpen }: { r: RunRow; onOpen: () => void }): JSX.Element {
  return (
    <div className="tpl-card" style={{ cursor: "pointer", alignItems: "flex-start" }} onClick={onOpen}>
      <Icon name="flow" />
      <div className="grow stack gap-4">
        <div className="row gap-8"><span className="strong">{r.name}</span><Badge tone={RUN_STATUS[r.status]?.tone ?? "muted"}>{RUN_STATUS[r.status]?.label ?? r.status}</Badge>
          {r.fatalDueAt && <span className="small text-warn">fatal {fmtDate(r.fatalDueAt)}</span>}</div>
        {r.statusNote && <div className="small text-bad">{r.statusNote}</div>}
        <div className="small muted">Agora:</div>
        <CurrentSteps r={r} />
        <div className="muted small">Iniciado por {r.startedBy} em {fmtDateTime(r.startedAt)}</div>
      </div>
      <button className="btn sm">Ver fluxo</button>
    </div>
  );
}

interface RunDetail {
  id: string; name: string; status: string; statusNote: string | null; graph: FlowGraph; roles: Record<string, string | null>; roleDefs: FlowRole[];
  fatalDueAt: string | null; startedAt: string; finishedAt: string | null; processId: string | null; caseId: string | null; personId: string | null;
  steps: Array<{ id: string; nodeId: string; status: StepMark; activityId: string | null; assigneeId: string | null; result: string | null; comment: string | null; waitUntil: string | null; error: string | null; startedAt: string; finishedAt: string | null }>;
  users: Array<{ id: string; displayName: string }>;
  activities: Array<{ id: string; number: number; state: string; internalDueAt: string | null; fatalDueAt: string | null }>;
}

export function RunModal({ id, onClose }: { id: string; onClose: () => void }): JSX.Element {
  const q = useQuery({ queryKey: ["bpm-execucao", id], queryFn: () => api.get<RunDetail>(`/bpm/execucoes/${id}`) });
  const inv = [["bpm-execucao", id], ["bpm-execucoes"], ["atividades"], ["bpm-painel"]];
  const cancel = useAct((reason: string) => api.post(`/bpm/execucoes/${id}/cancelar`, { reason }), inv, undefined, "Fluxo cancelado");
  const resume = useAct(() => api.post(`/bpm/execucoes/${id}/retomar`), inv, undefined, "Fluxo retomado");
  const [roles, setRoles] = useState<Record<string, string | null> | null>(null);
  const saveRoles = useAct(() => api.patch<{ moved: number }>(`/bpm/execucoes/${id}/papeis`, { roles }), inv, () => setRoles(null), "Papéis atualizados");
  const r = q.data;
  const marks = useMemo(() => {
    const m: Record<string, StepMark> = {};
    for (const s of r?.steps ?? []) m[s.nodeId] = s.status; // o último passo de cada bloco vale
    return m;
  }, [r]);
  const name = (uid: string | null) => r?.users.find((u) => u.id === uid)?.displayName ?? "—";
  const who = useMemo(() => {
    const w: Record<string, string> = {};
    if (!r) return w;
    for (const n of r.graph.nodes) if (n.type === "tarefa" && n.data.role) {
      const uid = r.roles[n.data.role];
      w[n.id] = uid ? `👤 ${r.users.find((u) => u.id === uid)?.displayName ?? "?"}` : "— pulada (sem pessoa)";
    }
    return w;
  }, [r]);
  const open = r && (r.status === "ativo" || r.status === "parado");
  return (
    <Modal title={r ? r.name : "Fluxo"} onClose={onClose} wide footer={<>
      {open && <button className="btn danger" disabled={cancel.isPending} onClick={() => void (async () => {
        const reason = window.prompt("Motivo do cancelamento (as tarefas abertas do fluxo também serão canceladas):");
        if (reason && reason.trim().length >= 3) cancel.mutate(reason.trim());
      })()}>Cancelar fluxo</button>}
      {r?.status === "parado" && <button className="btn primary" disabled={resume.isPending} onClick={() => resume.mutate()}>Retomar (pular o passo com problema)</button>}
      <button className="btn" onClick={onClose}>Fechar</button>
    </>}>
      <ErrorBox error={q.error ?? cancel.error ?? resume.error ?? saveRoles.error} />
      {!r ? <Loading /> : (
        <div className="stack gap-12">
          <div className="row gap-8 wrap">
            <Badge tone={RUN_STATUS[r.status]?.tone ?? "muted"}>{RUN_STATUS[r.status]?.label ?? r.status}</Badge>
            {r.fatalDueAt && <span className="small text-warn">Prazo fatal {fmtDate(r.fatalDueAt)}</span>}
            <span className="small muted">Início {fmtDateTime(r.startedAt)}{r.finishedAt ? ` · fim ${fmtDateTime(r.finishedAt)}` : ""}</span>
            <span className="grow" />
            <span className="small row gap-8">
              {(["ativo", "previsto", "aguardando", "concluido", "pulado", "erro"] as StepMark[]).map((m) => <span key={m} className="row gap-4"><span style={{ width: 10, height: 10, borderRadius: 5, background: MARK[m], display: "inline-block" }} />{m === "ativo" ? "agora" : m === "previsto" ? "criada, aguardando a vez" : m}</span>)}
            </span>
          </div>
          {r.statusNote && <div className="text-bad small">{r.statusNote}</div>}
          <div style={{ overflow: "auto", maxHeight: 460, border: "1px solid var(--border)", borderRadius: 8 }}>
            <FlowDiagram graph={r.graph} marks={marks} who={who} />
          </div>
          <div className="grid cols-2">
            <div>
              <div className="fld-label">Papéis</div>
              {r.roleDefs.map((d) => (
                <div key={d.key} className="row gap-8 small" style={{ marginBottom: 4 }}>
                  <span style={{ minWidth: 140 }}>{d.label}</span>
                  {roles ? <UserSelect value={roles[d.key] ?? null} onChange={(v) => setRoles({ ...roles, [d.key]: v })} emptyLabel="— ninguém —" /> : <span className="strong">{name(r.roles[d.key] ?? null)}</span>}
                </div>
              ))}
              {open && (roles
                ? <div className="row gap-8"><button className="btn sm" onClick={() => setRoles(null)}>Cancelar</button><button className="btn sm primary" disabled={saveRoles.isPending} onClick={() => saveRoles.mutate()}>Salvar papéis</button></div>
                : <button className="btn sm" onClick={() => setRoles({ ...r.roles })}>Trocar pessoas</button>)}
              {roles && <div className="muted small">Tarefas abertas do papel trocado passam para a nova pessoa; as próximas já nascem com ela.</div>}
            </div>
            <div>
              <div className="fld-label">Histórico</div>
              <div className="stack gap-4" style={{ maxHeight: 220, overflow: "auto" }}>
                {[...r.steps].reverse().map((s) => {
                  const n = r.graph.nodes.find((x) => x.id === s.nodeId);
                  const a = r.activities.find((x) => x.id === s.activityId);
                  return (
                    <div key={s.id} className="small">
                      <span style={{ width: 8, height: 8, borderRadius: 4, background: MARK[s.status] ?? "var(--muted)", display: "inline-block", marginRight: 6 }} />
                      <span className="strong">{n ? nodeText(n) : s.nodeId}</span>
                      {a && <> · <Link to={`/atividades/${a.id}`}>#{a.number}</Link></>}
                      {s.assigneeId && <span className="muted"> · {name(s.assigneeId)}</span>}
                      {s.result && <> · <Badge tone={/devol/i.test(s.result) ? "warn" : "ok"}>{s.result}</Badge></>}
                      <span className="muted"> · {fmtDateTime(s.finishedAt ?? s.startedAt)}</span>
                      {s.comment && <div className="muted" style={{ marginLeft: 14 }}>“{s.comment}”</div>}
                      {s.error && <div className="text-bad" style={{ marginLeft: 14 }}>{s.error}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

/** Painel "Fluxos em andamento" (BPM e bloco do Início). */
export function FlowsInProgress({ compact, mine }: { compact?: boolean; mine?: boolean }): JSX.Element {
  const [onlyMine, setOnlyMine] = useState(!!mine);
  const q = useQuery({ queryKey: ["bpm-execucoes", "andamento", onlyMine], queryFn: () => api.get<RunRow[]>(`/bpm/execucoes${qs({ status: "andamento", meus: onlyMine ? "1" : null })}`) });
  const [open, setOpen] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const filtered = (q.data ?? []).filter((r) => !filter || r.name === filter);
  const names = [...new Set((q.data ?? []).map((r) => r.name))];
  const ts = useTableSort("fluxos-andamento", filtered, {
    fluxo: (r) => r.name,
    onde: (r) => r.process?.cnjNumber ?? r.process?.title ?? (r.case ? `${r.case.title ?? ""}` : ""),
    inicio: (r) => new Date(r.startedAt),
    fatal: (r) => (r.fatalDueAt ? new Date(r.fatalDueAt) : null),
  });
  const list = ts.rows;
  return (
    <div className="stack gap-8">
      {!compact && (
        <div className="row gap-8 wrap">
          <select className="input" style={{ maxWidth: 280 }} value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="">Todos os fluxos</option>{names.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <label className="row gap-4 small"><input type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} /> Só os meus</label>
          <span className="muted small">{list.length} em andamento · {list.filter((r) => r.status === "parado").length} parado(s)</span>
        </div>
      )}
      <ErrorBox error={q.error} />
      {!q.data ? <Loading /> : !list.length ? <Empty icon="flow">Nenhum fluxo em andamento.</Empty> : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr>{ts.th("Fluxo", "fluxo")}{ts.th("Onde", "onde")}<th>Agora com</th>{!compact && ts.th("Fatal", "fatal")}<th /></tr></thead>
            <tbody>
              {list.slice(0, compact ? 8 : 300).map((r) => (
                <tr key={r.id} className={r.status === "parado" ? "row-bad" : ""}>
                  <td><span className="strong">{r.name}</span>{r.status === "parado" && <> <Badge tone="bad">parado</Badge></>}</td>
                  <td className="small">{where(r)}</td>
                  <td><CurrentSteps r={r} /></td>
                  {!compact && <td className="small">{fmtDate(r.fatalDueAt)}</td>}
                  <td><button className="btn sm" onClick={() => setOpen(r.id)}>Ver</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open && <RunModal id={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

// ------------------------------------------------------------------ passo do fluxo na atividade

export interface FlowStepInfo {
  runId: string; runName: string; runStatus: string; stepStatus: string; step: string; outcomes: string[]; result: string | null;
  notes: Array<{ step: string; result: string | null; comment: string | null; by: string | null; at: string }>;
}

export function useFlowStep(activityId: string) {
  return useQuery({ queryKey: ["bpm-passo", activityId], queryFn: () => api.get<FlowStepInfo | null>(`/bpm/atividades/${activityId}`) });
}

export function FlowStepCard({ activityId }: { activityId: string }): JSX.Element | null {
  const q = useFlowStep(activityId);
  const [open, setOpen] = useState(false);
  const s = q.data;
  if (!s) return null;
  return (
    <Card title={<span className="row gap-8"><Icon name="flow" size="sm" /> Parte do fluxo</span>} actions={<button className="btn sm" onClick={() => setOpen(true)}>Ver fluxo</button>}>
      <div className="stack gap-8">
        <div><span className="strong">{s.runName}</span> · passo “{s.step}” <Badge tone={RUN_STATUS[s.runStatus]?.tone ?? "muted"}>{RUN_STATUS[s.runStatus]?.label ?? s.runStatus}</Badge></div>
        {s.stepStatus === "previsto" && <div className="small" style={{ background: "var(--amber-50)", borderLeft: "3px solid var(--amber)", padding: "6px 8px", borderRadius: 4 }}>⏳ <strong>Aguardando etapa anterior.</strong> Esta tarefa já está programada para você, mas só pode ser feita quando o fluxo chegar nela — você recebe o aviso quando for liberada. As datas podem ser ajustadas na liberação.</div>}
        {s.outcomes.length > 0 && s.stepStatus === "ativo" && <div className="small">Ao concluir, escolha: <span className="strong">{s.outcomes.join(" / ")}</span>.</div>}
        {s.result && <div className="small">Resultado: <Badge tone={/devol/i.test(s.result) ? "warn" : "ok"}>{s.result}</Badge></div>}
        {s.notes.length > 0 && (
          <div className="stack gap-4">
            {s.notes.map((n, i) => (
              <div key={i} className="small" style={{ borderLeft: `3px solid ${/devol/i.test(n.result ?? "") ? "var(--amber)" : "var(--border)"}`, paddingLeft: 8 }}>
                <span className="strong">{n.step}</span>{n.result && <> · {n.result}</>}{n.by && <span className="muted"> · {n.by}</span>} <span className="muted">{fmtDateTime(n.at)}</span>
                <div>“{n.comment}”</div>
              </div>
            ))}
          </div>
        )}
      </div>
      {open && <RunModal id={s.runId} onClose={() => setOpen(false)} />}
    </Card>
  );
}

/** Escolha do resultado dentro da janela de concluir (Aprovar / Devolver…). */
export function OutcomePicker({ info, outcome, setOutcome, comment, setComment }: {
  info: FlowStepInfo; outcome: string; setOutcome: (v: string) => void; comment: string; setComment: (v: string) => void;
}): JSX.Element {
  const isReturn = /devol|corrig|refaz/i.test(outcome);
  return (
    <div className="stack gap-8" style={{ background: "var(--primary-50)", padding: 12, borderRadius: 8 }}>
      <div className="fld-label">Resultado (fluxo “{info.runName}”)</div>
      <div className="row gap-8 wrap">
        {info.outcomes.map((o) => (
          <button key={o} type="button" className={`btn ${outcome === o ? (/devol|corrig|refaz/i.test(o) ? "danger" : "primary") : ""}`} onClick={() => setOutcome(o)}>{o}</button>
        ))}
      </div>
      {outcome && (
        <Field label={isReturn ? "O que precisa ser corrigido" : "Comentário (opcional)"} required={isReturn}>
          <textarea className="input" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder={isReturn ? "Vai junto na tarefa que volta para quem redigiu." : ""} />
        </Field>
      )}
    </div>
  );
}

export function flowOutcomeBlocked(info: FlowStepInfo | null | undefined, outcome: string, comment: string): boolean {
  if (!info || info.stepStatus !== "ativo" || !info.outcomes.length) return false;
  if (!outcome) return true;
  return /devol|corrig|refaz/i.test(outcome) && !comment.trim();
}
